import { useSponsorCode } from '@/hooks/use-sponsor-code';
import { UserBlocker } from '@/hooks/use-user-blocker/types';
import {
    CircleCircle,
    CircleInvitation,
    CirclePartner,
    CirclePartnerConfig,
} from '@calm/evexia-web-sdk';
import { useAccessCode } from '@rhg/hooks/use-access-code';
import useAccount from '@rhg/hooks/use-account';
import { useActiveCircle } from '@rhg/hooks/use-active-circle';
import { useBuyUpEligibility } from '@rhg/hooks/use-buy-up-eligibility';
import { useDependentPartnerById } from '@rhg/hooks/use-dependent-partner';
import useHasAcceptedCohortTos from '@rhg/hooks/use-has-accepted-tos';
import { useInvitationByCode } from '@rhg/hooks/use-invitation';
import { useInviteCode } from '@rhg/hooks/use-invite-code';
import { useMostRecentSponsorship } from '@rhg/hooks/use-most-recent-sponsorship';
import { usePartnerByCode } from '@rhg/hooks/use-partner';
import {
    useActivationTokenForPartner,
    usePartnerActivation,
} from '@rhg/hooks/use-partner-activation';
import { usePartnerFromAccessCode } from '@rhg/hooks/use-partner-from-access-code';
import {
    RegistrationFlow,
    RegistrationFlowStep,
} from '@rhg/hooks/use-registration-context/types';
import {
    RegistrationDialogOpenQueryKey,
    useRegistrationDialog,
} from '@rhg/hooks/use-registration-dialog';
import {
    TransitionDestinationPartner,
    useTransitionPartner,
} from '@rhg/hooks/use-transition-partner';
import { useUpgradeDialog } from '@rhg/hooks/use-upgrade-dialog';
import { useUpgradeHandoffClaim } from '@rhg/hooks/use-upgrade-handoff-claim';
import { useUserBlocker } from '@rhg/hooks/use-user-blocker';
import { logger } from '@rhg/libs/logger';
import {
    accessCodeQueryString,
    sponsorCodeQueryString,
} from '@rhg/providers/magic-link';
import { getDestinationConsentStep } from '@rhg/utils/consent';
import { DEFAULT_COUNTRY_CODE } from '@rhg/utils/constants';
import {
    getInvitationStatus,
    InvitationErrorType,
    InvitationStatus,
} from '@rhg/utils/invitations';
import { getIsSponsorRelatedSponsorship } from '@rhg/utils/partner';
import { routePattern, routes } from '@rhg/utils/routes';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import { CountryErrorDialog } from '../../country-error-dialog';
import { InvitationErrorDialog } from '../../invitation-error-dialog';
import { SkipLinkingSSOQueryKey } from '../../linking-sso-step/types';

const RegistrationDialog = dynamic(() =>
    import('../../dialog').then((mod) => mod.RegistrationDialog)
);

const excludedPages = [
    routes.auth,
    routes.error({}),
    routes.expired,
    routes.notFound,
    routes.login,
    routes.standaloneScreening,
    routes.standaloneResults,
    routes.signup,
    routes.saml,
    routes.sso,
    routes.ssoInfo,
    routes.registration,
    routes.reconsent,
    routes.partnerMemberActivation,
    routePattern.partnerMemberActivationToken,
];

function getDefaultRegistrationFlow(
    isSponsored: boolean,
    isValidInvitation: boolean
) {
    if (isSponsored) {
        if (isValidInvitation) {
            return RegistrationFlow.ChangeOfInviteeSponsorship;
        } else {
            return RegistrationFlow.ChangeOfSponsorship;
        }
    } else {
        if (isValidInvitation) {
            return RegistrationFlow.ExistingInviteeRegistration;
        } else {
            return RegistrationFlow.ExistingRegistration;
        }
    }
}

function getDefaultRegistrationStep(
    isValidInvitation: boolean,
    hasAccessCode: boolean,
    hasSponsorCode: boolean,
    isSponsored: boolean,
    hasActivationToken: boolean
) {
    if (isValidInvitation) {
        return RegistrationFlowStep.ReceivingInvitation;
    } else if ((hasSponsorCode || hasActivationToken) && hasAccessCode) {
        return RegistrationFlowStep.AutoSubmitMagicLink;
    } else if (!hasAccessCode) {
        return RegistrationFlowStep.AccessCode;
    } else if (isSponsored) {
        return RegistrationFlowStep.ChangeSponsorship;
    } else {
        return RegistrationFlowStep.Eligibility;
    }
}

enum RegistrationDialogBlocker {
    Blocked = 'Blocked',
    ExcludedPage = 'ExcludedPage',
    NotReady = 'NotReady',
    NotOpen = 'NotOpen',
    Invalid = 'Invalid',
    InvitationExpired = 'InvitationExpired',
    InvitationClaimed = 'InvitationClaimed',
    InvitationCountry = 'InvitationCountry',
    Related = 'Related',
}

function getRegistrationDialogBlocker({
    isAccountFetched,
    isInviteCodeReady,
    isInviteCodeFromQuery,
    isInvitationFetched,
    isInvitationPartnerFetched,
    invitation,
    invitationError,
    invitationPartnerError,
    isUserBlockerLoading,
    isSkippingSSOLinking,
    userBlocker,
    isRouteReady,
    hasSponsorCode,
    hasActivationToken,
    isAccessCodeReady,
    isAccessCodeFromQuery,
    isActiveCircleLoading,
    isAccessCodePartnerFetched,
    isRegistrationDialogOpen,
    hasLostSponsorship,
    pathname,
    activeCircle,
    brandingPartnerConfig,
    isTransitioningToDestination,
    isTransitionPartnerLoading,
    destinationPartnerId,
    isDestinationPartnerConfigFetched,
}: {
    isAccountFetched: boolean;
    isInviteCodeReady: boolean;
    isInviteCodeFromQuery: boolean;
    isInvitationFetched: boolean;
    isInvitationPartnerFetched: boolean;
    isUserBlockerLoading: boolean;
    isSkippingSSOLinking: boolean;
    invitation?: CircleInvitation;
    invitationError?: InvitationErrorType;
    invitationPartner?: CirclePartner;
    invitationPartnerError?: InvitationErrorType;
    userBlocker?: UserBlocker;
    isRouteReady: boolean;
    hasSponsorCode: boolean;
    hasActivationToken: boolean;
    isAccessCodeReady: boolean;
    isAccessCodeFromQuery: boolean;
    isActiveCircleLoading: boolean;
    isAccessCodePartnerFetched: boolean;
    isRegistrationDialogOpen: boolean;
    hasLostSponsorship: boolean;
    pathname: string;
    activeCircle?: CircleCircle;
    brandingPartnerConfig?: CirclePartnerConfig | null;
    isTransitioningToDestination: boolean;
    isTransitionPartnerLoading: boolean;
    destinationPartnerId: string | null;
    isDestinationPartnerConfigFetched: boolean;
}): RegistrationDialogBlocker | null {
    const isReady =
        !isUserBlockerLoading &&
        isAccountFetched &&
        isRouteReady &&
        isAccessCodeReady &&
        !isActiveCircleLoading &&
        !isTransitionPartnerLoading &&
        (!isAccessCodeFromQuery || isAccessCodePartnerFetched) &&
        isInviteCodeReady &&
        (!isInviteCodeFromQuery ||
            (isInvitationFetched &&
                (!invitation || isInvitationPartnerFetched))) &&
        (!isTransitioningToDestination || isDestinationPartnerConfigFetched);

    if (!isRegistrationDialogOpen) {
        return RegistrationDialogBlocker.NotOpen;
    }

    if (excludedPages.includes(pathname)) {
        return RegistrationDialogBlocker.ExcludedPage;
    }

    // If the dependencies are not ready or if it is not supposed to be shown
    if (!isReady) {
        return RegistrationDialogBlocker.NotReady;
    }

    if (
        userBlocker &&
        [
            UserBlocker.AutoSubmitMagicLink,
            UserBlocker.CountryUnsponsored,
            UserBlocker.CountryUnsponsoredHasAccessCode,
            UserBlocker.Expired,
            UserBlocker.CCHDUnsponsored,
            ...(isSkippingSSOLinking ? [] : [UserBlocker.LinkingSSO]),
            UserBlocker.Tos,
            UserBlocker.ReceivingInvitation,
            UserBlocker.Reconsent,
        ].includes(userBlocker)
    ) {
        return RegistrationDialogBlocker.Blocked;
    }

    // If the access code is present but invalid without a sponsor code
    if (
        isAccessCodeFromQuery &&
        isAccessCodePartnerFetched &&
        !brandingPartnerConfig &&
        !hasSponsorCode &&
        !hasActivationToken
    ) {
        return RegistrationDialogBlocker.Invalid;
    }

    // If the access code is from a related (existing) sponsorship and the user has not lost sponsorship.
    // Skipped for the origin partner while transitioning: a matching sponsor
    // there means the user is upgrading that same sponsorship, not landing on
    // a no-op — the access code is about to be substituted for the
    // destination partner's own. Never skipped for the destination itself:
    // being sponsored by it already and re-entering the legacy code is a
    // plain no-op, same as re-entering your own current code directly.
    if (
        isAccessCodeFromQuery &&
        isAccessCodePartnerFetched &&
        brandingPartnerConfig &&
        !hasLostSponsorship &&
        (getIsSponsorRelatedSponsorship(
            activeCircle?.sponsor,
            destinationPartnerId ?? undefined
        ) ||
            (!isTransitioningToDestination &&
                getIsSponsorRelatedSponsorship(
                    activeCircle?.sponsor,
                    brandingPartnerConfig?.id
                )))
    ) {
        return RegistrationDialogBlocker.Related;
    }

    // If the invite is present but invalid/expired
    if (
        isInviteCodeFromQuery &&
        isInvitationFetched &&
        (!invitation ||
            [InvitationStatus.Expired, InvitationStatus.Deleted].includes(
                getInvitationStatus(invitation)
            ))
    ) {
        return RegistrationDialogBlocker.InvitationExpired;
    }

    // If the invite is from a different country than the user's current country
    if (
        isInviteCodeFromQuery &&
        ((isInvitationFetched && invitationError === 'country') ||
            (isInvitationPartnerFetched &&
                invitationPartnerError === 'country'))
    ) {
        return RegistrationDialogBlocker.InvitationCountry;
    }

    // If the invite is present but from the same user
    if (
        isInviteCodeFromQuery &&
        isInvitationFetched &&
        invitation?.senderRecipientId &&
        invitation?.senderRecipientId === activeCircle?.recipient?.id
    ) {
        return RegistrationDialogBlocker.Related;
    }

    // If the invite is present but claimed
    if (
        isInviteCodeFromQuery &&
        isInvitationFetched &&
        invitation &&
        getInvitationStatus(invitation) === InvitationStatus.Claimed
    ) {
        return RegistrationDialogBlocker.InvitationClaimed;
    }

    return null;
}

export const RegistrationDialogManager = () => {
    const { pathname, query, isReady: isRouteReady } = useRouter();
    const { account, isFetched: isAccountFetched } = useAccount();
    const {
        activeCircle,
        activeCircleId,
        isLoading: isActiveCircleLoading,
    } = useActiveCircle();
    const { isLoading: isUserBlockerLoading, userBlocker } = useUserBlocker();
    const {
        isInviteCodeFromQuery,
        inviteCode,
        isReady: isInviteCodeReady,
    } = useInviteCode();
    const {
        invitation,
        invitationError,
        isFetched: isInvitationFetched,
    } = useInvitationByCode(inviteCode);

    const {
        dependentPartner: invitationPartner,
        dependentPartnerError: invitationPartnerError,
        isFetched: isInvitationPartnerFetched,
    } = useDependentPartnerById({
        dependentPartnerId: invitation?.attributes?.partnerId,
        countryCode: account?.countryCode ?? DEFAULT_COUNTRY_CODE,
    });
    const {
        accessCode,
        isAccessCodeFromQuery,
        isReady: isAccessCodeReady,
    } = useAccessCode();
    const { hasSponsorCode, setSponsorCode } = useSponsorCode();
    const { hasActivationToken } = usePartnerActivation();
    const { brandingPartnerConfig, isFetched: isAccessCodePartnerFetched } =
        usePartnerFromAccessCode();
    const { hasActivationToken: hasActivationTokenForPartner } =
        useActivationTokenForPartner(brandingPartnerConfig?.id);

    const isSkippingSSOLinking = Boolean(query[SkipLinkingSSOQueryKey]);
    const {
        closeRegistrationDialog,
        isRegistrationDialogOpen,
        registrationDialogFlow,
        registrationDialogStep,
        showRegistrationDialog,
    } = useRegistrationDialog();
    const { hasLostSponsorship } = useMostRecentSponsorship();
    const { destinationPartner, isLoading: isTransitionPartnerLoading } =
        useTransitionPartner(brandingPartnerConfig);
    const isSponsored = Boolean(activeCircle?.sponsor);
    const { consent: accountConsent } = useHasAcceptedCohortTos();
    const accountConsents = accountConsent?.consentData
        ? [accountConsent.consentData]
        : [];
    const accountUserConsents = accountConsent?.userResponse
        ? [accountConsent.userResponse]
        : [];

    // Substituting `accessCode` flips `isAccessCodeFromQuery` false next
    // render, collapsing `destinationPartner` back to null — remember it so
    // the transition decision doesn't regress once substitution has fired.
    const destinationPartnerRef = useRef<TransitionDestinationPartner | null>(
        null
    );
    // The origin partner's id, captured before substitution — `/upgrade`
    // applies only when the current sponsor IS that origin partner.
    const originPartnerIdRef = useRef<string | null>(null);
    if (destinationPartner) {
        destinationPartnerRef.current = destinationPartner;
        originPartnerIdRef.current = brandingPartnerConfig?.id ?? null;
    }
    const isTransitioningToDestination =
        Boolean(destinationPartner) ||
        (destinationPartnerRef.current !== null &&
            destinationPartnerRef.current.slug === accessCode);
    const isUpgradingOwnSponsorship = Boolean(
        isSponsored &&
            originPartnerIdRef.current &&
            activeCircle?.sponsor?.partnerId === originPartnerIdRef.current
    );

    const {
        isEligible: isBuyUpEligible,
        isLoading: isBuyUpEligibilityLoading,
        destinationPartner: existingSponsorTransitionPartner,
    } = useBuyUpEligibility();
    const { showUpgradeDialog } = useUpgradeDialog();
    const { isAccessCodeClaimed } = useUpgradeHandoffClaim();
    // Prevents re-firing showUpgradeDialog for the same deep link — e.g.
    // dismissing the takeover clears `udo`, which would otherwise reopen it.
    const claimedUpgradeDeepLinkAccessCodeRef = useRef<string | null>(null);
    // Covers both deep-link shapes: isUpgradingOwnSponsorship resolves
    // before useBuyUpEligibility() does, and the cross-partner match can't
    // be known until it does either — so any sponsored deep link waits.
    // Without this, a transient "not eligible" locks in the old Eligibility/
    // ChangeOfSponsorship flow before the real eligible branch can claim it.
    const isUpgradeEligibilityPending = Boolean(
        isSponsored && brandingPartnerConfig && isBuyUpEligibilityLoading
    );
    // Either deep-link shape, gated off once UpgradeSponsorship is already
    // open so it doesn't re-claim a flow onUpgrade() just started. The
    // registrationDialogFlow check alone isn't enough: onUpgrade() seeds
    // accessCode synchronously but showRegistrationDialog()'s own query
    // write (which registrationDialogFlow reads) is async, so this can
    // still see the pre-write value in between. isAccessCodeClaimed closes
    // that gap with a synchronous signal from the same onUpgrade() call.
    const isEligibleUpgradeDeepLink = Boolean(
        brandingPartnerConfig &&
            isBuyUpEligible &&
            registrationDialogFlow !== RegistrationFlow.UpgradeSponsorship &&
            !isAccessCodeClaimed(accessCode) &&
            (isUpgradingOwnSponsorship ||
                (existingSponsorTransitionPartner &&
                    brandingPartnerConfig.id ===
                        existingSponsorTransitionPartner.id))
    );

    // The publicly-fetched `brandingPartnerConfig`/`destinationPartner` don't
    // carry consent requirements — those need the destination's full,
    // authenticated config, the same one the eventual registration
    // submission will validate eligibility against.
    const {
        partner: destinationPartnerConfig,
        isFetched: isDestinationPartnerConfigFetched,
    } = usePartnerByCode(activeCircleId, destinationPartnerRef.current?.slug);
    const destinationConsentStep = isTransitioningToDestination
        ? getDestinationConsentStep({
              accountConsents,
              accountUserConsents,
              destinationConsentConfigs:
                  destinationPartnerConfig?.consentConfigs,
          })
        : null;

    const [isDialogForceRendered, setIsDialogForceRendered] = useState(false);
    const [isErrorDialogOpen, setIsErrorDialogOpen] = useState(true);
    // Set at the bottom of this render, right before actually returning
    // <RegistrationDialog>, so the deep-link-deferral check below can tell
    // "already mid-flow" apart from "isDialogForceRendered flipped true on
    // this very render, alongside an eligible deep link" — the latter must
    // still defer to the Upgrade modal instead of opening its own dialog.
    const hasRenderedRegistrationDialogRef = useRef(false);

    const registrationDialogBlocker = getRegistrationDialogBlocker({
        isAccountFetched,
        isInviteCodeReady,
        isInviteCodeFromQuery,
        isInvitationFetched,
        isInvitationPartnerFetched,
        invitation: invitation ?? undefined,
        invitationError: invitationError ?? undefined,
        invitationPartner: invitationPartner ?? undefined,
        invitationPartnerError: invitationPartnerError ?? undefined,
        isUserBlockerLoading,
        isSkippingSSOLinking,
        userBlocker,
        isRouteReady,
        hasSponsorCode,
        hasActivationToken,
        isAccessCodeReady,
        isAccessCodeFromQuery,
        isActiveCircleLoading,
        isAccessCodePartnerFetched,
        isRegistrationDialogOpen,
        hasLostSponsorship: Boolean(hasLostSponsorship),
        pathname,
        activeCircle,
        brandingPartnerConfig,
        isTransitioningToDestination,
        isTransitionPartnerLoading,
        destinationPartnerId: destinationPartnerRef.current?.id ?? null,
        isDestinationPartnerConfigFetched,
    });

    // If we have already opened the dialog (meaning there is no blocker), we should keep showing the dialog
    useEffect(() => {
        if (!registrationDialogBlocker) {
            setIsDialogForceRendered(true);
        }
    }, [registrationDialogBlocker]);

    // We should not force the dialog to render if the blocker is set to ExcludedPage or if the dialog has been closed (this can happen if the user is force navigated to registration)
    useEffect(() => {
        if (
            registrationDialogBlocker ===
                RegistrationDialogBlocker.ExcludedPage ||
            !isRegistrationDialogOpen
        ) {
            setIsDialogForceRendered(false);
        }
    }, [isRegistrationDialogOpen, registrationDialogBlocker]);

    // Force close the dialog when it is blocked and not going to be used (this can happen i fht user is force navigated to FTUE/Concierge)
    useEffect(() => {
        if (
            registrationDialogBlocker &&
            [
                RegistrationDialogBlocker.Invalid,
                RegistrationDialogBlocker.Related,
            ].includes(registrationDialogBlocker) &&
            isRegistrationDialogOpen &&
            !isDialogForceRendered
        ) {
            void closeRegistrationDialog();
        }
    }, [
        closeRegistrationDialog,
        isDialogForceRendered,
        isRegistrationDialogOpen,
        registrationDialogBlocker,
    ]);

    // Deep-linked bought-up access code: skip straight to the destination's
    // consent/eligibility step, except a plain change of sponsorship (an
    // unrelated current sponsor) still lands on ChangeSponsorship to confirm
    // the switch. Dropping sponsor_code unconditionally avoids a race with
    // steps that read it synchronously at click-time (AutoSubmitMagicLinkStep,
    // ConsentStep, AccessCodeStep, ChangeSponsorship's own fallback).
    // Gated on the same readiness this manager uses to decide whether to
    // render, so it doesn't race UserBlockerGate's full-page flow for a
    // brand-new user (EVE-14056).
    useEffect(() => {
        if (
            (registrationDialogBlocker && !isDialogForceRendered) ||
            isInviteCodeFromQuery ||
            isUpgradeEligibilityPending
        ) {
            return;
        }

        // Hands off to the Upgrade modal instead of opening the
        // registration dialog. Always returns without falling through to
        // the destinationPartner branch below, even once already claimed.
        if (isEligibleUpgradeDeepLink) {
            if (claimedUpgradeDeepLinkAccessCodeRef.current !== accessCode) {
                claimedUpgradeDeepLinkAccessCodeRef.current = accessCode;
                setSponsorCode('');
                // Drop `rdo` so TransitionDialogManager doesn't mistake it
                // for a dialog this manager is rendering (neither would show
                // otherwise). Drop sponsor_code too — setSponsorCode alone
                // doesn't survive a reload.
                showUpgradeDialog({
                    dropQueryKeys: [
                        RegistrationDialogOpenQueryKey,
                        sponsorCodeQueryString,
                    ],
                }).catch((err: Error) =>
                    logger.error(
                        'transitionPartner.showUpgradeDialog.failed',
                        {},
                        err
                    )
                );
            }
            return;
        }

        if (destinationPartner && isDestinationPartnerConfigFetched) {
            // setSponsorCode (state, synchronous) wins over the query
            // immediately, before dropQueryKeys' own replace() below has
            // committed — closing the exact race the comment above
            // describes, rather than only hoping the navigation lands
            // before the next synchronous read of hasSponsorCode.
            setSponsorCode('');
            showRegistrationDialog({
                currentStep:
                    !isUpgradingOwnSponsorship && isSponsored
                        ? RegistrationFlowStep.ChangeSponsorship
                        : destinationConsentStep ??
                          RegistrationFlowStep.Eligibility,
                registrationFlow: isUpgradingOwnSponsorship
                    ? RegistrationFlow.UpgradeSponsorship
                    : isSponsored
                    ? RegistrationFlow.ChangeOfSponsorship
                    : RegistrationFlow.ExistingRegistration,
                forcedQuery: {
                    [accessCodeQueryString]: destinationPartner.slug,
                },
                dropQueryKeys: [sponsorCodeQueryString],
            }).catch((err: Error) =>
                logger.error(
                    'transitionPartner.deepLinkRedirect.failed',
                    {},
                    err
                )
            );
        }
    }, [
        accessCode,
        destinationPartner,
        registrationDialogBlocker,
        isDialogForceRendered,
        isDestinationPartnerConfigFetched,
        isInviteCodeFromQuery,
        isUpgradeEligibilityPending,
        destinationConsentStep,
        isUpgradingOwnSponsorship,
        isSponsored,
        isEligibleUpgradeDeepLink,
        showRegistrationDialog,
        showUpgradeDialog,
        setSponsorCode,
    ]);

    if (
        registrationDialogBlocker === RegistrationDialogBlocker.ExcludedPage ||
        (registrationDialogBlocker === RegistrationDialogBlocker.NotReady &&
            !isDialogForceRendered)
    ) {
        return null;
    }

    // Deferring to the Upgrade modal — also suppressed while eligibility is
    // still resolving, to avoid flashing the Eligibility form first. Not
    // when there's an invite code too: the effect above already leaves
    // that case alone, so this must too, or neither dialog renders. Not
    // once the dialog has actually rendered either: a later refetch of
    // useBuyUpEligibility (e.g. one the upgrade's own success triggers,
    // against the new sponsor) would otherwise unmount the in-progress
    // dialog, dropping its step state and falling back to defaults on
    // remount.
    if (
        (isEligibleUpgradeDeepLink || isUpgradeEligibilityPending) &&
        !isInviteCodeFromQuery &&
        !hasRenderedRegistrationDialogRef.current
    ) {
        return null;
    }

    if (
        registrationDialogBlocker ===
            RegistrationDialogBlocker.InvitationExpired &&
        isErrorDialogOpen
    ) {
        return (
            <InvitationErrorDialog
                type="expired"
                onClose={() => setIsErrorDialogOpen(false)}
            />
        );
    }

    if (
        registrationDialogBlocker ===
            RegistrationDialogBlocker.InvitationCountry &&
        isErrorDialogOpen
    ) {
        return (
            <CountryErrorDialog onClose={() => setIsErrorDialogOpen(false)} />
        );
    }

    if (
        registrationDialogBlocker ===
            RegistrationDialogBlocker.InvitationClaimed &&
        isErrorDialogOpen
    ) {
        return (
            <InvitationErrorDialog
                type="claimed"
                onClose={() => setIsErrorDialogOpen(false)}
            />
        );
    }

    const defaultRegistrationFlow = isTransitioningToDestination
        ? isUpgradingOwnSponsorship
            ? RegistrationFlow.UpgradeSponsorship
            : isSponsored
            ? RegistrationFlow.ChangeOfSponsorship
            : RegistrationFlow.ExistingRegistration
        : getDefaultRegistrationFlow(
              Boolean(activeCircle?.sponsor),
              Boolean(invitation && invitationPartner)
          );
    const defaultRegistrationStep = isTransitioningToDestination
        ? !isUpgradingOwnSponsorship && isSponsored
            ? RegistrationFlowStep.ChangeSponsorship
            : destinationConsentStep ?? RegistrationFlowStep.Eligibility
        : getDefaultRegistrationStep(
              Boolean(invitation && invitationPartner),
              isAccessCodeFromQuery,
              hasSponsorCode,
              Boolean(activeCircle?.sponsor),
              hasActivationTokenForPartner
          );

    if (!registrationDialogBlocker || isDialogForceRendered) {
        hasRenderedRegistrationDialogRef.current = true;
        return (
            <RegistrationDialog
                closeDialog={() => {
                    closeRegistrationDialog().catch((err: Error) =>
                        logger.error(
                            'Error closing registration dialog',
                            {},
                            err
                        )
                    );
                }}
                registrationFlow={
                    registrationDialogFlow || defaultRegistrationFlow
                }
                currentStep={registrationDialogStep || defaultRegistrationStep}
                transitionDestinationPartner={
                    destinationPartnerRef.current ?? undefined
                }
            />
        );
    }
};
